import { useFeatureIsOn } from "@growthbook/growthbook-react";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import WidthFullOutlinedIcon from "@mui/icons-material/WidthFullOutlined";
import WidthNormalOutlinedIcon from "@mui/icons-material/WidthNormalOutlined";
import WidthWideOutlinedIcon from "@mui/icons-material/WidthWideOutlined";
import {
  FormControlLabel,
  IconButton,
  Button as MuiButton,
  Stack,
  Switch,
  Tooltip,
} from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { FeatureFlagBoundary } from "@platform/fe-utils";
import { PPX_ADD_PLAN_MODAL, PPX_QUOTE_HISTORY } from "@platform/feature-flags";
import { CoverageTypeEnum } from "@platform/types";
import { useState } from "react";

import { AddPlanModal } from "../modals/AddPlanModal";
import { CoverageBenefitPlanSelector } from "../selectors/CoverageBenefitPlanSelector";

import type {
  BenefitPlanGroup,
  ColumnWidthPreset,
} from "@platform/btx-plan-designer";

// ---------------------------------------------------------------------------
// Toolbar — extracted to reduce component complexity
// ---------------------------------------------------------------------------

interface QuotesToolbarProps {
  ecoRfpId: string;
  brokerPortalRfpId: string | undefined;
  benefitPlanGroupsByCoverage: Partial<
    Record<CoverageTypeEnum, BenefitPlanGroup[]>
  >;
  availableCoverageTypes: CoverageTypeEnum[];
  isLoadingAvailableCoverageTypes: boolean;
  unmappedCountByCoverage: Partial<Record<CoverageTypeEnum, number>>;
  selectedCoverageType?: CoverageTypeEnum;
  selectedBpgId?: string;
  onPlanSelect: (coverageType: CoverageTypeEnum, bpgId?: string) => void;
  columnWidth: ColumnWidthPreset;
  onColumnWidthChange: (preset: ColumnWidthPreset) => void;
  isFullScreen: boolean;
  onToggleFullScreen: VoidFunction;
  showLegend: boolean;
  onShowLegendChange: (show: boolean) => void;
  showHistory: boolean;
  /** Opens the carrier-selection dialog when off, exits history when on. */
  onHistoryButtonClick: VoidFunction;
  /**
   * Warms the carrier picker's data on hover or focus, before the dialog is opened. Its
   * query is the all-versions scope, which nothing else on the board fetches, so without
   * this the dialog always opens onto a spinner.
   */
  onHistoryButtonPrefetch: VoidFunction;
  onAIExtract: VoidFunction;
}

const AIExtractButton = ({
  onClick,
}: {
  onClick: VoidFunction;
}): React.JSX.Element => (
  <Button
    variant="tertiary"
    size="sm"
    startIcon={<FileUploadOutlinedIcon />}
    onClick={onClick}
  >
    AI Extract
  </Button>
);

export const QuotesToolbar = ({
  ecoRfpId,
  brokerPortalRfpId,
  benefitPlanGroupsByCoverage,
  availableCoverageTypes,
  isLoadingAvailableCoverageTypes,
  unmappedCountByCoverage,
  selectedCoverageType,
  selectedBpgId,
  onPlanSelect,
  columnWidth,
  onColumnWidthChange,
  isFullScreen,
  onToggleFullScreen,
  showLegend,
  onShowLegendChange,
  showHistory,
  onHistoryButtonClick,
  onHistoryButtonPrefetch,
  onAIExtract,
}: QuotesToolbarProps): React.JSX.Element => {
  const [addPlanOpen, setAddPlanOpen] = useState(false);
  const addPlanEnabled = useFeatureIsOn(PPX_ADD_PLAN_MODAL);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <CoverageBenefitPlanSelector
            benefitPlanGroupsByCoverage={benefitPlanGroupsByCoverage}
            availableCoverageTypes={availableCoverageTypes}
            isLoadingAvailableCoverageTypes={isLoadingAvailableCoverageTypes}
            unmappedCountByCoverage={unmappedCountByCoverage}
            selectedCoverageType={selectedCoverageType}
            selectedBpgId={selectedBpgId}
            onSelect={onPlanSelect}
          />
          {addPlanEnabled && (
            <Button
              variant="tertiary"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => setAddPlanOpen(true)}
            >
              Add plan
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Narrow columns">
            <IconButton
              size="small"
              onClick={() => onColumnWidthChange("narrow")}
              color={columnWidth === "narrow" ? "primary" : "default"}
            >
              <WidthNormalOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Medium columns">
            <IconButton
              size="small"
              onClick={() => onColumnWidthChange("medium")}
              color={columnWidth === "medium" ? "primary" : "default"}
            >
              <WidthWideOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Wide columns">
            <IconButton
              size="small"
              onClick={() => onColumnWidthChange("wide")}
              color={columnWidth === "wide" ? "primary" : "default"}
            >
              <WidthFullOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullScreen ? "Exit full screen" : "Full screen"}>
            <IconButton
              size="small"
              onClick={onToggleFullScreen}
              aria-label={isFullScreen ? "Exit full screen" : "Full screen"}
              aria-pressed={isFullScreen}
            >
              {isFullScreen ? (
                <FullscreenExitOutlinedIcon fontSize="small" />
              ) : (
                <FullscreenOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <FeatureFlagBoundary flag={PPX_QUOTE_HISTORY}>
            <MuiButton
              variant={showHistory ? "contained" : "outlined"}
              size="small"
              color="brandPrimary"
              onClick={onHistoryButtonClick}
              onMouseEnter={onHistoryButtonPrefetch}
              onFocus={onHistoryButtonPrefetch}
            >
              {showHistory ? "Hide Quote History" : "View Quote History"}
            </MuiButton>
          </FeatureFlagBoundary>
          <FormControlLabel
            control={
              <Switch
                color="brandPrimary"
                size="small"
                checked={showLegend}
                onChange={(e) => onShowLegendChange(e.target.checked)}
              />
            }
            label={<Typography variant="body-medium">Legend</Typography>}
            labelPlacement="end"
            sx={{ ml: 0 }}
          />
          <AIExtractButton onClick={onAIExtract} />
        </Stack>
      </Stack>
      {addPlanEnabled && (
        <AddPlanModal
          open={addPlanOpen}
          onClose={() => setAddPlanOpen(false)}
          rfpId={ecoRfpId}
          brokerPortalRfpId={brokerPortalRfpId}
          selectedCoverageType={selectedCoverageType}
          onPlanAdded={onPlanSelect}
        />
      )}
    </>
  );
};
